import { letterCount, paraFormat, textRuns } from '../analysis/format';
import type { Role } from '../analysis/structure';
import { DEFAULT_EAST_ASIA_FONT, hintedEastAsiaFont, runFonts, type Paragraph } from '../model/document';
import { lineMultiple, type Origin } from '../model/props';
import { GOST_2_105, GOST_7_32 } from '../profiles/ids';
import { countOf } from '../util/text';
import { cmToTwips, fmtCm, fmtNum, fmtPt } from '../util/units';
import { defineRule, type FixAction, type Rule, type RuleContext } from './types';
import { ALIGN_RU, byRole, colorInfo, FONT_ROLES, mainFont, mainTextSize, normFont, originNote, PARAS, SPECIAL_FONTS, TNR_EQUIVALENTS } from './util';

const SIZE_ROLES = new Set<Role>(['body', 'list', 'note', 'formula-where', 'bib', 'caption', 'heading', 'appendix-title']);
const EXACT_SIZE_ROLES = new Set<Role>(['body', 'list', 'note', 'formula-where', 'bib']);
const SPACING_ROLES = new Set<Role>(['body', 'list', 'note', 'bib', 'formula-where']);
const INDENT_ROLES = new Set<Role>(['body', 'note']);
const COLOR_ROLES = new Set<Role>(['front', 'body', 'list', 'note', 'formula-where', 'bib', 'caption', 'heading', 'appendix-title', 'table-cell', 'toc']);
const SIGN_ROLES = new Set<Role>(['body', 'list', 'note']);

/** ЕСКД: кегль приложений, примечаний и сносок при данном кегле основного текста (полупункты). */
const AUX_SIZE: Readonly<Record<number, number>> = { 28: 24, 26: 24, 22: 20 };

const fmtSizes = (sizes: number[]): string => sizes.map((s) => fmtNum(s / 2, 1)).join(', ') + ' пт';

function runSizes(ctx: RuleContext, p: Paragraph): number[] {
  const sizes = new Set<number>();
  for (const r of textRuns(p)) {
    if (r.props.vertAlign === 'superscript' || r.props.vertAlign === 'subscript') continue;
    sizes.add(r.props.sz ?? ctx.model.styles.defaultsR.sz ?? 20);
  }
  return [...sizes].sort((a, b) => a - b);
}

/** Полужирные буквы абзаца без вводной метки «Цель работы:» или «Примечание —»: её выделяют намеренно. */
function boldLetters(p: Paragraph): number {
  const label = /^\s*(?:[^:\n]{2,40}:|Примечани[ея]\s*\d*\.?\s*[-–—]?)/.exec(p.text);
  const from = label ? label[0].length : 0;
  let n = 0;
  for (const r of p.runs) {
    if (!r.props.b || r.end <= from) continue;
    n += letterCount(p.text.slice(Math.max(r.start, from), r.end));
  }
  return n;
}

/** Слова рядом со знаком: предыдущее и следующее. */
function neighbours(text: string, start: number, end: number): [string, string] {
  const prev = /(\S+)\s*$/.exec(text.slice(0, start))?.[1] ?? '';
  const next = /^\s*(\S+)/.exec(text.slice(end))?.[1] ?? '';
  return [prev, next];
}

/** Русское слово целиком: «больше ≥ нормы», но не переменная «Т3 > Т2». */
const RU_WORD = /^[«(]?[А-ЯЁа-яё]{3,}[»),.;:!?]?$/;
const CLOSING: Readonly<Record<string, string>> = { '(': ')', '[': ']', '{': '}' };

/** Минус внутри вектора, списка аргументов или координат: «[1, -0.5]», «logspace(-3, 2)», «(0 0 -0,05)». */
function inNumberList(text: string, at: number): boolean {
  let open = -1;
  for (let k = at - 1; k >= 0 && at - k <= 80; k--) {
    const ch = text[k]!;
    if (ch === ')' || ch === ']' || ch === '}') return false;
    if (CLOSING[ch]) {
      open = k;
      break;
    }
  }
  if (open < 0) return false;
  if (text[open] !== '(') return true;
  const close = text.indexOf(')', at);
  const inner = text.slice(open + 1, close < 0 ? undefined : close);
  return (inner.match(/\d+(?:[.,]\d+)?/g) ?? []).length >= 2;
}

export const textRules: Rule[] = [
  defineRule({
    id: 'text.font',
    category: 'text',
    title: 'Шрифт',
    severity: 'warning',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 5.1.1' },
    why: {
      [GOST_7_32]: 'Рекомендуемый шрифт основного текста — Times New Roman; методички обычно делают его обязательным. Гарнитура должна быть одинаковой во всём тексте.',
      [GOST_2_105]: 'Гарнитура — Times New Roman или Arial. Использовать разные гарнитуры в одном документе не рекомендуется.',
    },
    check(ctx) {
      const { model, s, profile, report } = ctx;
      const allowed = new Set(profile.text.fonts.map(normFont));
      const paras = model.paragraphs.filter((p) => !p.textBox && !p.inToc && FONT_ROLES.has(s.roles[p.index]!));
      // Основная гарнитура — самая частая из допустимых; ею и исправляем.
      const { font: target, main } = mainFont(ctx);
      const targetIsTnr = normFont(target) === 'times new roman';
      const others: number[] = [];
      const otherNames = new Set<string>();
      for (const p of paras) {
        const bad = new Set<string>();
        let onlyEquivalents = true;
        let other = false;
        let origin: Origin | undefined;
        let eastAsia: string | undefined;
        for (const r of textRuns(p)) {
          const text = p.text.slice(r.start, r.end);
          const hinted = hintedEastAsiaFont(model.styles, r.props, text);
          for (const font of runFonts(model, r, text)) {
            const nf = normFont(font);
            if (SPECIAL_FONTS.test(nf)) continue;
            if (allowed.has(nf)) {
              if (main && nf !== main) {
                other = true;
                otherNames.add(profile.text.fonts.find((f) => normFont(f) === nf) ?? font);
              }
              continue;
            }
            bad.add(font);
            if (!(targetIsTnr && TNR_EQUIVALENTS.has(nf))) onlyEquivalents = false;
            if (font === hinted) eastAsia ??= font;
            else origin ??= r.origin.fontHAnsi ?? r.origin.fontAscii;
          }
        }
        if (!bad.size) {
          if (other) others.push(p.index);
          continue;
        }
        const fonts = [...bad].join(', ');
        report({
          severity: onlyEquivalents && !eastAsia ? 'info' : undefined,
          paragraph: p.index,
          message: eastAsia
            ? `Русские буквы выводятся ${eastAsia === DEFAULT_EAST_ASIA_FONT ? 'восточноазиатским шрифтом, который подставит Word' : `восточноазиатским шрифтом ${eastAsia}`}: текст помечен как азиатский — нужен ${target}`
            : onlyEquivalents
            ? `Шрифт ${fonts} совместим с ${target} по размерам, но на компьютере проверяющего может замениться другим`
            : `Шрифт ${fonts}${originNote(model, origin)} — нужен ${target}`,
          fix: [{ kind: 'runs', paragraph: p.index, set: { font: target } }],
          fixLabel: `Шрифт ${target}`,
        });
      }
      if (others.length) {
        report({
          severity: 'info',
          paragraph: others[0],
          message: `${countOf(others.length, ['абзац набран', 'абзаца набраны', 'абзацев набраны'])} шрифтом ${[...otherNames].join(', ')}, а остальной текст — ${target}. Разные гарнитуры в одном документе не рекомендуются`,
          fix: others.map((i): FixAction => ({ kind: 'runs', paragraph: i, set: { font: target } })),
          fixLabel: `Шрифт ${target} во всём тексте`,
        });
      }
    },
  }),

  defineRule({
    id: 'text.size',
    category: 'text',
    title: 'Размер шрифта',
    severity: 'error',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 5.1.1' },
    why: {
      [GOST_7_32]: 'Размер шрифта — не менее 12 пт. Многие методички требуют ровно 14 пт для основного текста. В таблицах допускается меньший кегль.',
      [GOST_2_105]: 'Основной текст — 14 пт (допускается 13 или 11), приложения, примечания, сноски и примеры — 12 пт (10 при основном 11). Разные сочетания размеров в одном документе не допускаются.',
    },
    check(ctx) {
      const { model, s, profile, report } = ctx;
      const min = profile.text.minSizePt * 2;
      const exact = profile.text.sizePt ? profile.text.sizePt * 2 : undefined;
      const allowed = (profile.text.allowedSizesPt ?? []).map((v) => v * 2);
      // Кегль основного текста: точный из профиля или один из допустимых — тот, которым набран документ.
      let main = exact;
      let aux: number | undefined;
      let mainWrong = false;
      if (main === undefined && allowed.length) {
        const dom = mainTextSize(ctx);
        mainWrong = dom === undefined || !allowed.includes(dom);
        main = mainWrong ? allowed[0]! : dom!;
        aux = AUX_SIZE[main] ?? main - 4;
      }
      const alternatives = allowed
        .slice(1)
        .map((v) => fmtNum(v / 2))
        .join(' или ');
      for (const p of model.paragraphs) {
        const role = s.roles[p.index]!;
        if (p.textBox || p.cell || !SIZE_ROLES.has(role)) continue;
        const sizes = runSizes(ctx, p);
        const small = sizes.filter((sz) => sz < min);
        if (small.length) {
          const to = role === 'heading' || role === 'caption' ? Math.max(min, main ?? min) : (main ?? Math.max(min, 28));
          report({
            paragraph: p.index,
            message: `Кегль ${fmtSizes(small)} — нужно не менее ${fmtNum(min / 2)} пт`,
            fix: [{ kind: 'runs', paragraph: p.index, set: { sz: to } }],
            fixLabel: `Кегль ${fmtNum(to / 2)} пт`,
          });
          continue;
        }
        if (main === undefined) continue;
        // Приложения, примечания и подписи можно набирать уменьшенным кеглем.
        const secondary = aux !== undefined && (role === 'note' || role === 'caption' || s.regions[p.index] === 'appendix');
        if (!EXACT_SIZE_ROLES.has(role) && !secondary) continue;
        const off = sizes.filter((sz) => sz !== main && !(secondary && sz === aux));
        if (!off.length) continue;
        const to = secondary && off.every((sz) => sz < main!) ? aux! : main;
        report({
          severity: 'warning',
          paragraph: p.index,
          message:
            exact !== undefined
              ? `Кегль ${fmtSizes(off)} — нужно ${fmtNum(exact / 2)} пт`
              : mainWrong
                ? `Кегль ${fmtSizes(off)} — основной текст набирают ${fmtNum(main / 2)} пт${alternatives ? ` (допускается ${alternatives} пт)` : ''}`
                : `Кегль ${fmtSizes(off)}, а основной текст набран ${fmtNum(main / 2)} пт — разные размеры шрифта в одном документе не допускаются`,
          fix: [{ kind: 'runs', paragraph: p.index, set: { sz: to } }],
          fixLabel: `Кегль ${fmtNum(to / 2)} пт`,
        });
      }
    },
  }),

  defineRule({
    id: 'text.line-spacing',
    category: 'text',
    title: 'Межстрочный интервал',
    severity: 'error',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 5.1.2' },
    why: {
      [GOST_7_32]: 'Текст печатают через полтора интервала. «Точно 18 пт» или «множитель 1,15» — это не полуторный интервал.',
      [GOST_2_105]: 'Текст оформляют с полуторным межстрочным интервалом, допускается двойной. «Точно 18 пт» или «множитель 1,15» — это не полуторный интервал.',
    },
    check(ctx) {
      const want = ctx.profile.text.lineSpacing;
      const alts = ctx.profile.text.lineSpacingAlt ?? [];
      const ok = [want, ...alts];
      const name = (v: number): string => (v === 1.5 ? 'полуторный' : v === 2 ? 'двойной' : v === 1 ? 'одинарный' : fmtNum(v, 2));
      const wanted = name(want) + (alts.length ? ` (допускается ${alts.map(name).join(', ')})` : '');
      for (const p of byRole(ctx, SPACING_ROLES)) {
        const mult = lineMultiple(p.props);
        if (mult !== undefined && ok.some((v) => Math.abs(mult - v) < 0.03)) continue;
        const actual =
          mult !== undefined
            ? `${fmtNum(mult, 2)}`
            : `${p.props.lineRule === 'exact' ? 'точно' : 'минимум'} ${fmtPt((p.props.line ?? 0) / 20)}`;
        ctx.report({
          paragraph: p.index,
          message: `Межстрочный интервал ${actual}${originNote(ctx.model, p.origin.line)} — нужен ${wanted}`,
          fix: [{ kind: 'para', paragraph: p.index, set: { line: Math.round(want * 240), lineRule: 'auto' } }],
          fixLabel: `Интервал ${fmtNum(want, 2)}`,
        });
      }
    },
  }),

  defineRule({
    id: 'text.indent',
    category: 'text',
    title: 'Абзацный отступ',
    severity: 'error',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 5.1.4' },
    why: {
      [GOST_7_32]: 'Абзацный отступ одинаковый по всему тексту и равен 1,25 см.',
      [GOST_2_105]: 'Абзацный отступ — пять знаков шрифта, от 1,25 до 1,7 см, и одинаковый по всему тексту.',
    },
    check(ctx) {
      const t = ctx.profile.text;
      const want = cmToTwips(t.firstLineIndentCm);
      const max = t.firstLineIndentMaxCm !== undefined ? Math.max(want, cmToTwips(t.firstLineIndentMaxCm)) : want;
      const inRange = (v: number): boolean => v >= want - 20 && v <= max + 20;
      const paras = byRole(ctx, INDENT_ROLES).filter(
        // Отступ пробелами — отдельное правило; центрированные строки отступа не имеют.
        (p) => !/^[ \t]{2,}|^\t/.test(p.text) && p.props.jc !== 'center' && p.props.jc !== 'right',
      );
      // При допустимом диапазоне отступ всё равно одинаковый: ориентир — самый частый в тексте.
      let common = want;
      if (max > want) {
        const freq = new Map<number, number>();
        for (const p of paras) {
          const f = p.props.indFirst ?? 0;
          if (!inRange(f) || Math.abs(p.props.indLeft ?? 0) > 20) continue;
          const key = Math.round(f / 10) * 10;
          freq.set(key, (freq.get(key) ?? 0) + 1);
        }
        common = [...freq].sort((a, b) => b[1] - a[1])[0]?.[0] ?? want;
      }
      const range = max > want ? `${fmtNum(t.firstLineIndentCm)}–${fmtNum(t.firstLineIndentMaxCm!)} см` : `${fmtNum(t.firstLineIndentCm)} см`;
      for (const p of paras) {
        const first = p.props.indFirst ?? 0;
        const left = p.props.indLeft ?? 0;
        const firstOk = inRange(first);
        const leftOk = Math.abs(left) <= 20;
        const uniform = Math.abs(first - common) <= 20;
        if (firstOk && leftOk && uniform) continue;
        const parts: string[] = [];
        if (!firstOk) parts.push(first === 0 ? 'нет абзацного отступа' : first < 0 ? `выступ ${fmtCm(-first)}` : `отступ ${fmtCm(first)}`);
        if (!leftOk) parts.push(`отступ слева ${fmtCm(left)}`);
        ctx.report({
          paragraph: p.index,
          message:
            firstOk && leftOk
              ? `Абзацный отступ ${fmtCm(first)}, а в остальном тексте ${fmtCm(common)} — отступ должен быть одинаковым`
              : `${parts.join(', ')} — нужен отступ первой строки ${range}`,
          fix: [{ kind: 'para', paragraph: p.index, set: { indFirst: common, indLeft: 0 } }],
          fixLabel: `Отступ ${fmtCm(common)}`,
        });
      }
    },
  }),

  defineRule({
    id: 'text.align',
    category: 'text',
    title: 'Выравнивание по ширине',
    severity: 'info',
    aggregate: PARAS,
    why: 'Стандарт выравнивание не регламентирует, но почти все методички требуют основной текст по ширине.',
    check(ctx) {
      const want = ctx.profile.text.alignment;
      if (want === 'any') return;
      for (const p of byRole(ctx, INDENT_ROLES)) {
        const jc = p.props.jc ?? 'left';
        if (jc === want || p.text.trim().length < 80) continue;
        // Абзац целиком в квадратных скобках — пометка для автора («[Место для рисунка: …]»), а не текст.
        if (/^\[[^\]]*\]$/.test(p.text.trim())) continue;
        ctx.report({
          paragraph: p.index,
          message: `Абзац выровнен ${ALIGN_RU[jc]} — нужно ${ALIGN_RU[want]}`,
          fix: [{ kind: 'para', paragraph: p.index, set: { jc: want } }],
          fixLabel: `Выровнять ${ALIGN_RU[want]}`,
        });
      }
    },
  }),

  defineRule({
    id: 'text.spacing',
    category: 'text',
    title: 'Интервалы между абзацами',
    severity: 'info',
    aggregate: PARAS,
    why: 'Интервалы «перед» и «после» абзаца стандарт не задаёт, но методички обычно требуют 0 пт. У стиля «Обычный» в новых версиях Word по умолчанию 8 пт после абзаца.',
    check(ctx) {
      const before = ctx.profile.text.spacingBeforePt;
      const after = ctx.profile.text.spacingAfterPt;
      if (before === undefined && after === undefined) return;
      for (const p of byRole(ctx, SPACING_ROLES)) {
        if (p.props.contextualSpacing) continue;
        const b = p.props.beforeAuto ? 280 : (p.props.spacingBefore ?? 0);
        const a = p.props.afterAuto ? 280 : (p.props.spacingAfter ?? 0);
        const parts: string[] = [];
        if (before !== undefined && Math.abs(b - before * 20) > 20) parts.push(`перед абзацем ${fmtPt(b / 20)}`);
        if (after !== undefined && Math.abs(a - after * 20) > 20) parts.push(`после абзаца ${fmtPt(a / 20)}`);
        if (!parts.length) continue;
        ctx.report({
          paragraph: p.index,
          message: `Интервал ${parts.join(', ')}${originNote(ctx.model, p.origin.spacingAfter ?? p.origin.spacingBefore)} — обычно требуется 0 пт`,
          fix: [{ kind: 'para', paragraph: p.index, set: { spacingBefore: (before ?? 0) * 20, spacingAfter: (after ?? 0) * 20 } }],
          fixLabel: 'Убрать интервалы',
        });
      }
    },
  }),

  defineRule({
    id: 'text.color',
    category: 'text',
    title: 'Чёрный цвет текста',
    severity: 'warning',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: '' },
    why: 'Цвет шрифта должен быть чёрным. Частые нарушения: синие гиперссылки в списке литературы и в содержании, серые подписи рисунков.',
    check({ model, s, report }) {
      const toc: number[] = [];
      const tocNames = new Set<string>();
      let tocLinks = false;
      for (const p of model.paragraphs) {
        if (p.textBox || !COLOR_ROLES.has(s.roles[p.index]!)) continue;
        const f = paraFormat(model, p);
        if (!f.colors.size) continue;
        const names = new Set<string>();
        let nearBlackOnly = true;
        let links = false;
        for (const r of textRuns(p)) {
          const c = r.props.color;
          if (!c || c === 'auto' || c === '000000') continue;
          const info = colorInfo(c);
          names.add(info.name);
          if (!info.nearBlack) nearBlackOnly = false;
          if (r.inHyperlink) links = true;
        }
        if (!names.size) continue;
        // Пункты содержания обычно окрашены все сразу — это одно замечание, а не десяток.
        if (s.roles[p.index] === 'toc') {
          toc.push(p.index);
          for (const n of names) tocNames.add(n);
          tocLinks ||= links;
          continue;
        }
        report({
          severity: nearBlackOnly ? 'info' : undefined,
          paragraph: p.index,
          message: `Цвет текста: ${[...names].join(', ')}${links ? ' (гиперссылка)' : ''} — нужен чёрный`,
          fix: [{ kind: 'runs', paragraph: p.index, set: links ? { color: 'auto', underline: false } : { color: 'auto' } }],
          fixLabel: 'Сделать чёрным',
        });
      }
      if (toc.length) {
        report({
          paragraph: toc[0],
          message: `Цвет пунктов содержания: ${[...tocNames].join(', ')} (${countOf(toc.length, ['пункт', 'пункта', 'пунктов'])}) — нужен чёрный`,
          fix: toc.map((i): FixAction => ({ kind: 'runs', paragraph: i, set: tocLinks ? { color: 'auto', underline: false } : { color: 'auto' } })),
          fixLabel: 'Сделать чёрным',
        });
      }
    },
  }),

  defineRule({
    id: 'text.highlight',
    category: 'text',
    title: 'Выделение цветом',
    severity: 'warning',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: '' },
    why: 'Выделение маркером и заливка текста — следы работы над текстом, в сдаваемой работе их быть не должно.',
    check(ctx) {
      for (const p of ctx.model.paragraphs) {
        if (p.textBox || ctx.s.roles[p.index] === 'front') continue;
        const f = paraFormat(ctx.model, p);
        if (f.highlighted < 2) continue;
        ctx.report({
          paragraph: p.index,
          message: 'Текст выделен маркером или заливкой',
          fix: [{ kind: 'runs', paragraph: p.index, set: { highlight: false } }],
          fixLabel: 'Убрать выделение',
        });
      }
    },
  }),

  defineRule({
    id: 'text.bold',
    category: 'text',
    title: 'Полужирный только в заголовках',
    severity: 'info',
    aggregate: PARAS,
    gost: 'ГОСТ 7.32-2017, п. 6.1.1',
    why: 'Полужирный шрифт применяют только для заголовков разделов, подразделов и структурных элементов. Для акцентов допустимо другое начертание того же кегля — например, курсив. Вводные слова вроде «Цель работы:» не считаются.',
    check(ctx) {
      for (const p of byRole(ctx, new Set<Role>(['body', 'list', 'note']))) {
        const bold = boldLetters(p);
        if (bold < 4) continue;
        const f = paraFormat(ctx.model, p);
        const whole = bold / Math.max(1, f.letters) > 0.9 && p.text.trim().length < 150;
        ctx.report({
          paragraph: p.index,
          message: whole
            ? 'Весь абзац полужирный. Если это заголовок, пронумеруйте его и примените стиль «Заголовок»'
            : 'Полужирное выделение в основном тексте',
        });
      }
    },
  }),

  defineRule({
    id: 'text.underline',
    category: 'text',
    title: 'Без подчёркивания',
    severity: 'info',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.1, 6.2.3', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.6.2' },
    why: 'Заголовки не подчёркивают; подчёркивание в основном тексте тоже не принято.',
    check(ctx) {
      for (const p of byRole(ctx, new Set<Role>(['body', 'list', 'note', 'heading', 'caption']))) {
        let n = 0;
        for (const r of textRuns(p)) {
          if (r.inHyperlink || !r.props.u || r.props.u === 'none') continue;
          n += letterCount(p.text.slice(r.start, r.end));
        }
        if (n < 3) continue;
        ctx.report({
          paragraph: p.index,
          message: ctx.s.roles[p.index] === 'heading' ? 'Заголовок подчёркнут' : 'Подчёркнутый текст',
          fix: [{ kind: 'runs', paragraph: p.index, set: { underline: false } }],
          fixLabel: 'Убрать подчёркивание',
        });
      }
    },
  }),

  defineRule({
    id: 'text.signs',
    category: 'text',
    title: 'Знаки вместо слов',
    severity: 'info',
    gost: 'ГОСТ Р 2.105-2019, п. 5.2.4',
    why: 'В тексте, кроме формул, таблиц и рисунков, не применяют знак «-» перед отрицательными числами (пишут «минус»), знак «Ø» (пишут «диаметр»), математические знаки, «№» и «%» без чисел, обозначения стандартов без номера.',
    check(ctx) {
      for (const p of byRole(ctx, SIGN_ROLES)) {
        const text = p.text;
        const marks: Array<[number, number]> = [];
        const fix: FixAction[] = [];
        const kinds = new Set<string>();
        const hit = (start: number, end: number, kind: string): void => {
          marks.push([start, end]);
          kinds.add(kind);
        };
        for (const m of text.matchAll(/(?<=^|[\s(])[-−–](?=\d)/g)) {
          if (inNumberList(text, m.index)) continue;
          hit(m.index, m.index + 1, 'знак «-» перед числом — пишут слово «минус»');
          fix.push({ kind: 'text', paragraph: p.index, start: m.index, end: m.index + 1, expect: m[0], replace: 'минус ' });
        }
        for (const m of text.matchAll(/[Ø⌀](\s?)(?=\d)/g)) {
          hit(m.index, m.index + 1, 'знак «Ø» — пишут слово «диаметр»');
          fix.push({ kind: 'text', paragraph: p.index, start: m.index, end: m.index + m[0].length, expect: m[0], replace: 'диаметр ' });
        }
        for (const m of text.matchAll(/(?<=\s)[<>=≠≤≥](?=\s)/g)) {
          const [prev, next] = neighbours(text, m.index, m.index + 1);
          if (RU_WORD.test(prev) && RU_WORD.test(next)) hit(m.index, m.index + 1, `знак «${m[0]}» без чисел — пишут словами`);
        }
        // «№ группы» и «в %» — знаки без чисел; «%d», «%pi» в описании программ не трогаем.
        for (const m of text.matchAll(/№/g)) {
          if (/^\s*\p{L}/u.test(text.slice(m.index + 1))) hit(m.index, m.index + 1, 'знак «№» без номера');
        }
        for (const m of text.matchAll(/%/g)) {
          if (!/\d\s*$/.test(text.slice(0, m.index)) && !/^[A-Za-z{(]/.test(text.slice(m.index + 1))) hit(m.index, m.index + 1, 'знак «%» без числа');
        }
        for (const m of text.matchAll(/(?<!\p{L})ГОСТ(?:у|а|ом|е|ы|ов|ам|ами|ах)?(?!\p{L})(?!(?:\s*(?:Р|ISO|ИСО|IEC|МЭК|EN|\/))*\s*\d)/gu)) {
          hit(m.index, m.index + m[0].length, 'обозначение стандарта без номера');
        }
        if (!marks.length) continue;
        ctx.report({
          paragraph: p.index,
          marks,
          message: `В тексте ${[...kinds].join('; ')}`,
          fix: fix.length ? fix : undefined,
          fixLabel: 'Заменить словами',
        });
      }
    },
  }),
];
